import { FieldValues, useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { BaseSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select';
import { useAsyncOptions } from '@/shared/components/ui/form/components/searchable-select/hooks/use-async-options/use-async-options';
import { useSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/hooks/use-searchable-select/use-searchable-select';
import { TAsyncSearchableSelectProps } from '@/shared/components/ui/form/components/searchable-select/variant/async/async-searchable-select.types';
import { useDebounce } from '@/shared/hooks/use-debounce/use-debounce';

export const AsyncSearchableSelect = <
  TFieldValues extends FieldValues,
  TData,
  TOptionShape,
>({
  name,
  optionProps,
  onSelectChange,
  defaultValue,
  renderNoOptions,
  getItemId,
  disableSort,
  multiple,
  clearSearchOnSelect,
  hint,
  ...props
}: TAsyncSearchableSelectProps<TFieldValues, TData, TOptionShape>) => {
  const { t } = useTranslation();
  const { shouldSearch, inputValue, handleSelectChange, handleInputChange } =
    useSearchableSelect({ onSelectChange, multiple, clearSearchOnSelect });
  const debouncedSearch = useDebounce({
    value: shouldSearch ? inputValue : '',
    // Only typing is debounced; dropping the term must reach the query at once.
    delay: shouldSearch ? undefined : 0,
  });
  const { control, resetField } = useFormContext<TFieldValues>();
  const {
    field: { value },
  } = useController({
    name,
    control,
    defaultValue,
  });

  const { options, loading, isSelected, loadError, hasMoreOptions } =
    useAsyncOptions({
      ...optionProps,
      getItemId,
      debouncedSearch,
      value,
      disableSort,
    });

  // A truncated page matters more than a caller's static hint.
  const getHint = () => {
    if (hasMoreOptions) {
      return t('ui:select_more_options_hint', { count: optionProps.pageSize });
    }
    return hint;
  };

  const getRenderNoOptions = () => {
    if (typeof renderNoOptions === 'function') {
      return renderNoOptions(debouncedSearch, () => resetField(name));
    }

    return renderNoOptions;
  };

  return (
    <BaseSearchableSelect
      {...props}
      name={name}
      onSelectChange={handleSelectChange}
      defaultValue={defaultValue}
      renderNoOptions={getRenderNoOptions()}
      getItemId={getItemId}
      multiple={multiple}
      options={options}
      loading={loading}
      loadError={loadError}
      isSelected={isSelected}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      hint={getHint()}
    />
  );
};
